/**
 * Partnerbereich: Einschulungstermin buchen
 *
 * Schritt 1: Studio waehlen, freien 60-Minuten-Termin antippen
 *            (Wochenansicht am PC, Tagesansicht mit Datumsleiste am Handy)
 * Schritt 2: Kunde eintragen (Vorname, Nachname, E-Mail, Einverstaendnis)
 * Schritt 3: Bestaetigung
 *
 * Welche Zeiten frei sind, entscheidet das Backend (partner.service.js).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format, addDays, startOfWeek, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { partnerAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Partner.css';

const iso = (d) => format(d, 'yyyy-MM-dd');
const LEER = { vorname: '', nachname: '', email: '', einschulungDurch: '', einverstanden: false };

// Wer macht die Einschulung? (Martins Feedback 5.11.2026)
const DURCH_OPTIONEN = [
  { wert: 'physio', titel: 'Ich schule meinen Patienten selbst ein', text: 'nach dem Therapieplan' },
  { wert: 'franz', titel: 'Keine Einschulung durch mich nötig', text: 'FRANZ zeigt das Studio und erklärt die Ausstattung' }
];
const durchLabel = (wert, physio) => (wert === 'franz' ? 'FRANZ (Studio zeigen, Ausstattung erklären)' : `${physio || 'Physio'} (nach Therapieplan)`);

const PartnerTermin = () => {
  const { user } = useAuth();
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState(null);
  const [wochenStart, setWochenStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [tagIndex, setTagIndex] = useState(() => (new Date().getDay() + 6) % 7);
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [gewaehlt, setGewaehlt] = useState(null); // { datum, start, ende }
  const [schritt, setSchritt] = useState('wahl'); // wahl | kunde | fertig
  const [kunde, setKunde] = useState(LEER);
  const [sendet, setSendet] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);

  useEffect(() => {
    partnerAPI.getStudios()
      .then(res => {
        setStudios(res.data);
        if (res.data.length) setStudioId(res.data[0].id);
      })
      .catch(err => setFehler(err.response?.data?.error || 'Studios konnten nicht geladen werden'));
  }, []);

  const ladeSlots = useCallback(async () => {
    if (!studioId) return;
    setLaedt(true);
    setFehler('');
    try {
      const res = await partnerAPI.getSlots(studioId, iso(wochenStart), iso(addDays(wochenStart, 7)));
      setDaten(res.data);
    } catch (err) {
      setFehler(err.response?.data?.error || 'Termine konnten nicht geladen werden');
    }
    setLaedt(false);
  }, [studioId, wochenStart]);

  useEffect(() => { ladeSlots(); }, [ladeSlots]);

  const studio = studios.find(s => s.id === studioId);
  const heute = new Date();
  const ersteWoche = startOfWeek(heute, { weekStartsOn: 1 });
  const vorlauf = daten?.vorlaufTage || 14;
  const kannZurueck = wochenStart > ersteWoche;
  const kannVor = addDays(wochenStart, 7) <= addDays(heute, vorlauf);

  const woche = (delta) => {
    setWochenStart(w => addDays(w, 7 * delta));
    setGewaehlt(null);
    setTagIndex(delta > 0 ? 0 : 6);
  };

  const studioWechseln = (id) => {
    setStudioId(id);
    setGewaehlt(null);
  };

  const istGewaehlt = (datum, start) => gewaehlt?.datum === datum && gewaehlt?.start === start;

  const slotKnopf = (tag, slot, kompakt) => {
    const frei = slot.status === 'frei';
    const klasse = `pt-slot ${slot.status} ${istGewaehlt(tag.datum, slot.start) ? 'gewaehlt' : ''}`;
    return (
      <button
        key={slot.start}
        type="button"
        className={klasse}
        disabled={!frei}
        onClick={() => setGewaehlt({ datum: tag.datum, start: slot.start, ende: slot.ende })}
        title={frei ? `${slot.start}–${slot.ende} buchen` : slot.status === 'belegt' ? 'belegt' : 'nicht buchbar'}
      >
        {kompakt ? slot.start : <><span className="pt-slot-zeit">{slot.start}–{slot.ende}</span><span className="pt-slot-status">{frei ? 'frei' : slot.status === 'belegt' ? 'belegt' : '–'}</span></>}
      </button>
    );
  };

  const buchen = async (e) => {
    e.preventDefault();
    setFehler('');
    if (!kunde.einschulungDurch) {
      setFehler('Bitte auswählen, wer die Einschulung macht.');
      return;
    }
    if (!kunde.einverstanden) {
      setFehler('Bitte bestätige das Einverständnis deines Patienten.');
      return;
    }
    setSendet(true);
    try {
      const res = await partnerAPI.buchen({
        studioId,
        datum: gewaehlt.datum,
        start: gewaehlt.start,
        vorname: kunde.vorname,
        nachname: kunde.nachname,
        email: kunde.email,
        einschulungDurch: kunde.einschulungDurch,
        einverstanden: kunde.einverstanden
      });
      setErgebnis(res.data.einschulung);
      setSchritt('fertig');
    } catch (err) {
      setFehler(err.response?.data?.error || 'Die Buchung hat nicht geklappt. Bitte versuche es erneut.');
      // Termin inzwischen weg? Dann Kalender neu laden
      if (err.response?.status === 409) {
        setGewaehlt(null);
        setSchritt('wahl');
        ladeSlots();
      }
    }
    setSendet(false);
  };

  const neueBuchung = () => {
    setKunde(LEER);
    setGewaehlt(null);
    setErgebnis(null);
    setSchritt('wahl');
    ladeSlots();
  };

  const datumLang = (datum) => format(parseISO(datum), 'EEEE, d. MMMM yyyy', { locale: de });

  // ----- Schritt 3: Bestaetigung -----
  if (schritt === 'fertig' && ergebnis) {
    return (
      <div className="partner-page">
        <div className="pt-card pt-fertig">
          <div className="pt-fertig-icon" aria-hidden="true">✓</div>
          <h1>Termin gebucht – danke!</h1>
          <dl className="pt-zusammenfassung">
            <dt>Kunde</dt><dd>{ergebnis.kunde}<br /><span className="pt-muted">{ergebnis.email}</span></dd>
            <dt>Studio</dt><dd>{ergebnis.studio}</dd>
            <dt>Datum</dt><dd>{datumLang(ergebnis.datum)}</dd>
            <dt>Uhrzeit</dt><dd>{ergebnis.start}–{ergebnis.ende} Uhr</dd>
            <dt>Einschulung</dt><dd>{durchLabel(ergebnis.einschulungDurch, user?.name)}</dd>
          </dl>
          <p className="pt-weiter-text">
            <strong>Wie geht es jetzt weiter?</strong> Sowohl dein Patient, du als auch wir vom FRANZ erhalten eine
            Bestätigung per E-Mail mit allen relevanten Daten (Termin, Ort und wer die Einschulung vornimmt).
          </p>
          <div className="pt-aktionen">
            <Link to="/partner/einschulungen" className="pt-btn pt-btn-sekundaer">Meine Einschulungen</Link>
            <button type="button" className="pt-btn pt-btn-primaer" onClick={neueBuchung}>Weitere Einschulung buchen</button>
          </div>
        </div>
      </div>
    );
  }

  // ----- Schritt 2: Kunde eintragen -----
  if (schritt === 'kunde' && gewaehlt) {
    return (
      <div className="partner-page">
        <h1 className="pt-titel">Kunde eintragen</h1>
        <div className="pt-card">
          <div className="pt-kontrolle">
            <div><span>Studio</span><strong>{studio?.name}</strong></div>
            <div><span>Datum</span><strong>{datumLang(gewaehlt.datum)}</strong></div>
            <div><span>Uhrzeit</span><strong>{gewaehlt.start}–{gewaehlt.ende} Uhr</strong></div>
          </div>

          <form onSubmit={buchen} className="pt-form">
            <div className="pt-feldreihe">
              <label>
                Vorname *
                <input value={kunde.vorname} onChange={e => setKunde({ ...kunde, vorname: e.target.value })} required autoComplete="off" />
              </label>
              <label>
                Nachname *
                <input value={kunde.nachname} onChange={e => setKunde({ ...kunde, nachname: e.target.value })} required autoComplete="off" />
              </label>
            </div>
            <label>
              E-Mail des Kunden *
              <input type="email" value={kunde.email} onChange={e => setKunde({ ...kunde, email: e.target.value })} required autoComplete="off" />
            </label>

            <fieldset className="pt-durch">
              <legend>Wer macht die Einschulung? *</legend>
              {DURCH_OPTIONEN.map(o => (
                <label key={o.wert} className={`pt-durch-option ${kunde.einschulungDurch === o.wert ? 'aktiv' : ''}`}>
                  <input
                    type="radio"
                    name="einschulungDurch"
                    value={o.wert}
                    checked={kunde.einschulungDurch === o.wert}
                    onChange={() => setKunde({ ...kunde, einschulungDurch: o.wert })}
                  />
                  <span><strong>{o.titel}</strong><br />{o.text}</span>
                </label>
              ))}
            </fieldset>

            <label className="pt-checkbox">
              <input type="checkbox" checked={kunde.einverstanden} onChange={e => setKunde({ ...kunde, einverstanden: e.target.checked })} />
              <span>Mein Patient ist einverstanden, dass Der Starke Franz ihn zum Abschluss eines Mitgliedsvertrags kontaktiert.</span>
            </label>

            <div className="pt-hinweis">
              <strong>Nur Erstbuchung:</strong> Pro Kunde ist ein Einschulungstermin möglich. Das System erkennt bereits
              eingeschulte Kunden an der E-Mail-Adresse. Folgetermine vereinbart ihr direkt miteinander.
            </div>

            {fehler && <div className="pt-fehler">{fehler}</div>}

            <div className="pt-aktionen">
              <button type="button" className="pt-btn pt-btn-sekundaer" onClick={() => { setSchritt('wahl'); setFehler(''); }} disabled={sendet}>
                Abbrechen
              </button>
              <button type="submit" className="pt-btn pt-btn-primaer" disabled={sendet}>
                {sendet ? 'Wird gebucht…' : 'Termin verbindlich buchen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ----- Schritt 1: Termin waehlen -----
  const tage = daten?.tage || [];
  const zeiten = tage[0]?.slots.map(s => s.start) || [];
  const tagMobil = tage[tagIndex] || tage[0];

  return (
    <div className="partner-page">
      <h1 className="pt-titel">Einschulungstermin buchen</h1>
      <p className="pt-untertitel">Eine Einschulung dauert 60 Minuten. Buchbar bis {vorlauf} Tage im Voraus.</p>

      <div className="pt-studio-schalter" role="tablist">
        {studios.map(s => (
          <button key={s.id} type="button" role="tab" aria-selected={s.id === studioId}
            className={s.id === studioId ? 'active' : ''} onClick={() => studioWechseln(s.id)}>
            <strong>{s.name}</strong>
            {s.address && <span>{s.address}</span>}
          </button>
        ))}
      </div>

      <div className="pt-card">
        <div className="pt-wochenkopf">
          <button type="button" className="pt-pfeil" onClick={() => woche(-1)} disabled={!kannZurueck} aria-label="Vorherige Woche">‹</button>
          <span>
            {format(wochenStart, 'd. MMM', { locale: de })} – {format(addDays(wochenStart, 6), 'd. MMM yyyy', { locale: de })}
          </span>
          <button type="button" className="pt-pfeil" onClick={() => woche(1)} disabled={!kannVor} aria-label="Nächste Woche">›</button>
        </div>

        {fehler && <div className="pt-fehler">{fehler}</div>}
        {laedt && !daten && <div className="pt-laedt">Lade Termine…</div>}

        {tage.length > 0 && (
          <>
            {/* PC/Tablet: Wochenansicht */}
            <div className={`pt-woche ${laedt ? 'laedt' : ''}`}>
              <div className="pt-woche-grid" style={{ gridTemplateColumns: `56px repeat(${tage.length}, minmax(0, 1fr))` }}>
                <div />
                {tage.map(t => (
                  <div key={t.datum} className={`pt-tagkopf ${t.datum === iso(heute) ? 'heute' : ''}`}>
                    <span>{format(parseISO(t.datum), 'EEE', { locale: de })}</span>
                    <strong>{format(parseISO(t.datum), 'd.M.')}</strong>
                  </div>
                ))}
                {zeiten.map((zeit, zi) => (
                  <React.Fragment key={zeit}>
                    <div className="pt-zeitlabel">{zeit}</div>
                    {tage.map(t => slotKnopf(t, t.slots[zi], true))}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Handy: Datumsleiste + Tagesansicht */}
            <div className={`pt-tag ${laedt ? 'laedt' : ''}`}>
              <div className="pt-datumsleiste">
                {tage.map((t, i) => (
                  <button key={t.datum} type="button" className={i === tagIndex ? 'active' : ''} onClick={() => setTagIndex(i)}>
                    <span>{format(parseISO(t.datum), 'EEE', { locale: de })}</span>
                    <strong>{format(parseISO(t.datum), 'd.')}</strong>
                  </button>
                ))}
              </div>
              {tagMobil && (
                <div className="pt-tagesliste">
                  {tagMobil.slots.filter(s => s.status !== 'gesperrt').length === 0
                    ? <p className="pt-muted">An diesem Tag ist nichts mehr buchbar.</p>
                    : tagMobil.slots.filter(s => s.status !== 'gesperrt').map(s => slotKnopf(tagMobil, s, false))}
                </div>
              )}
            </div>

            <div className="pt-legende">
              <span><i className="frei" /> frei</span>
              <span><i className="belegt" /> belegt</span>
              <span><i className="gewaehlt" /> gewählt</span>
            </div>
          </>
        )}
      </div>

      {gewaehlt && (
        <div className="pt-weiter-leiste">
          <span>{format(parseISO(gewaehlt.datum), 'EEE, d. MMM', { locale: de })} · {gewaehlt.start}–{gewaehlt.ende} Uhr</span>
          <button type="button" className="pt-btn pt-btn-primaer" onClick={() => { setSchritt('kunde'); setFehler(''); }}>
            Weiter mit {gewaehlt.start} Uhr
          </button>
        </div>
      )}
    </div>
  );
};

export default PartnerTermin;
